import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Handshake, Search, LayoutGrid, List, ChevronDown } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { useToast } from "src/components/ui/toast"

import { usePipelines, useDeals, useChangeStage } from "src/features/deals/hooks/useDeals"
import { DealsKanban } from "src/features/deals/components/DealsKanban"
import { DealsTable } from "src/features/deals/components/DealsTable"
import { formatMoney } from "src/features/deals/constants"

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function DealsPage() {
  const { toast } = useToast()
  const { pipelines, loading: pipelinesLoading } = usePipelines()
  const { data, loading, fetch } = useDeals()
  const changeStage = useChangeStage()

  const [view, setView] = useState("kanban") // "kanban" | "table"
  const [pipelineId, setPipelineId] = useState(null)
  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)

  // Default to the first (default) pipeline once loaded.
  useEffect(() => {
    if (!pipelineId && pipelines.length) {
      setPipelineId((pipelines.find((p) => p.is_default) || pipelines[0]).id)
    }
  }, [pipelines, pipelineId])

  const activePipeline = useMemo(
    () => pipelines.find((p) => p.id === pipelineId) || null,
    [pipelines, pipelineId]
  )

  const stages = useMemo(
    () => (activePipeline?.stages || []).slice().sort((a, b) => a.position - b.position),
    [activePipeline]
  )

  const reload = useCallback(() => {
    if (pipelineId) fetch({ pipeline_id: pipelineId, q, per_page: 200 })
  }, [pipelineId, q, fetch])

  useEffect(() => { reload() }, [reload])

  const deals = data?.deals ?? []
  const openDeals = useMemo(() => deals.filter((d) => d.status === "open"), [deals])
  const closedCount = deals.length - openDeals.length
  const totalValue = useMemo(
    () => openDeals.reduce((s, d) => s + (Number(d.value) || 0), 0),
    [openDeals]
  )

  // Optimistic stage move on the Kanban board.
  async function handleMoveDeal(deal, stage) {
    try {
      await changeStage.mutate(deal.id, stage.id)
      reload()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not move deal"
      toast({ variant: "destructive", title: "Move failed", description: msg })
      reload()
    }
  }

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        {/* ── Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Handshake className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Deals</h1>
              <p className="text-[13px] text-slate-500">
                {openDeals.length} open · {formatMoney(totalValue)} in pipeline
                {closedCount > 0 ? ` · ${closedCount} closed` : ""}
              </p>
            </div>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setView("kanban")}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[13px] font-medium transition ${view === "kanban" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Board
            </button>
            <button
              onClick={() => setView("table")}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[13px] font-medium transition ${view === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-xs flex-1">
            <Search style={{ left: "0.75rem" }} className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              placeholder="Search deals…"
              value={searchRaw}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {pipelines.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between gap-2 font-normal text-slate-700">
                  {activePipeline?.name || "Pipeline"}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuRadioGroup
                  value={String(pipelineId || "")}
                  onValueChange={(v) => setPipelineId(Number(v))}
                >
                  {pipelines.map((p) => (
                    <DropdownMenuRadioItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* ── Content ── */}
        {(pipelinesLoading || (loading && !data)) ? (
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-80 w-72 rounded-2xl" />
            ))}
          </div>
        ) : stages.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
            This pipeline has no stages yet.
          </div>
        ) : view === "kanban" ? (
          <DealsKanban stages={stages} deals={deals} onMoveDeal={handleMoveDeal} />
        ) : (
          <DealsTable deals={deals} loading={loading} />
        )}
      </div>
    </AppShell>
  )
}
